import { toNotificationModel } from '../model/mappers/notification.model';
import { toNotificationSearchRequestDto } from '../model/mappers/search-request.dto';

import type { Notification } from '../model/types/notification.dto';
import type { NotificationModel } from '../model/types/notification.model';
import type { NotificationSearchRequestDto } from '../model/types/search-request.dto';
import type { NotificationSearchRequestModel } from '../model/types/search-request.model';
import type { MessageCreateModel } from '@/features/Shared/model/types/message-create.model';
import type { SearchResponseDto } from '@/features/Shared/model/types/search-response.dto';
import type { SearchResponseModel } from '@/features/Shared/model/types/search-response.model';

import { apiSlice } from '@/app/api/api';
import { toMessageCreateDto } from '@/features/Shared/model/mappers/message-create.dto';

export const notificationsApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		notifications: builder.infiniteQuery<
			SearchResponseModel<NotificationModel>,
			NotificationSearchRequestModel,
			string | undefined
		>({
			infiniteQueryOptions: {
				initialPageParam: undefined,
				getNextPageParam: (lastPage: SearchResponseModel<NotificationModel>) => {
					return lastPage.moreResults ? (lastPage.continuation ?? undefined) : undefined;
				},
			},

			query: ({ queryArg, pageParam }) => ({
				url: '/notifications/search',
				method: 'POST',
				body: {
					...toNotificationSearchRequestDto(queryArg),
					continuation_token: pageParam ?? undefined,
				} satisfies NotificationSearchRequestDto,
			}),
			transformResponse: (response: SearchResponseDto<Notification>) => {
				return {
					items: (response.items ?? []).map(toNotificationModel),
					continuation: response.meta.continuation,
					moreResults: response.meta.more_results,
				};
			},
		}),
		createNotification: builder.mutation<void, MessageCreateModel>({
			query: (item) => {
				return {
					url: `/notifications`,
					method: 'POST',
					body: toMessageCreateDto(item),
				};
			},
			invalidatesTags: () => [{ type: 'NOTIFICATIONS', id: 'PARTIAL-LIST' }],
		}),
		deleteNotification: builder.mutation<void, { id: string; organizationId: string }>({
			query: ({ id, organizationId }) => {
				return {
					url: `/organizations/${organizationId}/notifications/${id}`,
					method: 'DELETE',
				};
			},
			invalidatesTags: () => [{ type: 'NOTIFICATIONS', id: 'PARTIAL-LIST' }],
		}),
	}),

	overrideExisting: false,
});

export const {
	useNotificationsInfiniteQuery,
	useCreateNotificationMutation,
	useDeleteNotificationMutation,
} = notificationsApiSlice;
